import { AlertController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import Order from '../models/Order';
import { getAbsolutValueFromTipSelection, numberD, paymentFromOrder, paymentMethodsFromVenue } from './utils';
import { SelectPaymentMethodModalComponent } from '../../app/modals/select-payment-method-modal/select-payment-method-modal.component';
import { PaymentMethod } from '../enums/PaymentMethod';
import { AdyenCredentials } from '../models/Venue';
import { CreditCardModalComponent } from '../../app/modals/credit-card-modal/credit-card-modal.component';
import { RepositoryService } from '../services/repository/repository.service';
import { Router } from '@angular/router';
import { CashPaymentModalComponent, CashPaymentResult } from '../../app/modals/cash-payment-modal/cash-payment-modal.component';
import { PaymentStatus } from '../models/PaymentStatus';
import dayjs from 'dayjs';
import { firstValueFrom } from 'rxjs';
import { PaymentTimeoutModalComponent } from '../../app/modals/payment-timeout-modal/payment-timeout-modal.component';
import { environment } from '../../environments/environment';
import { GooglePayModalComponent } from '../../app/modals/google-pay-modal/google-pay-modal.component';
import { ApplePayModalComponent } from '../../app/modals/apple-pay-modal/apple-pay-modal.component';
import { PaypalModalComponent, PaypalModalResult } from '../../app/modals/paypal-modal/paypal-modal.component';
import { IframeCommunicationService } from '../services/communication/iframe-communication.service';
import { OrderStatus } from '../enums/OrderStatus';
import PiggyGiftCard from '../models/PiggyGiftCard';
import { TipSelectorValue } from '../../app/components/tip-selector/tip-selector.component';
import { PaySessionStatistics } from '../models/PaySessionStatistics';
import { TipSelectionModalComponent } from '../../app/modals/tip-selection-modal/tip-selection-modal.component';

export class PaymentHandler {
	constructor(
		private modalCtrl: ModalController,
		private alertCtrl: AlertController,
		private router: Router,
		private repositoryService: RepositoryService,
		private venueReadableId: string | null,
		private onLoad: (loading: boolean) => void,
		private iframeCommunication?: IframeCommunicationService,
		private translate?: TranslateService
	) {}

	static generateApplePaySession(order: Order, totalPrice: number, adyenCredentials: AdyenCredentials): ApplePaySession {
		const paymentRequest = {
			countryCode: 'DE',
			currencyCode: 'EUR',
			environment: environment.applePayEnvironment,
			amount: { currency: 'EUR', value: totalPrice },
			total: {
				label: 'Merchant',
				amount: totalPrice,
				type: 'final',
			},
			clientKey: adyenCredentials.clientKey,
			supportedNetworks: ['visa', 'masterCard', 'discover', 'maestro', 'amex'],
			merchantCapabilities: ['supports3DS'],
			version: 3,
			displayName: order.venueName,
			checkAvailability: 'canMakePayments',
			merchantIdentifier: environment.applePayMerchant,
			style: 'black',
			shippingType: 'storePickup',
		};
		console.log('ApplePaySessionPaymentRequest', paymentRequest);
		//@ts-ignore
		return new ApplePaySession(3, paymentRequest);
	}

	async startPayment(): Promise<void> {
		console.log('startPayment()');
		// The whole generate + pay sequence is one atomic critical section — see
		// RepositoryService.runExclusivePayment. A duplicate trigger while this is running is
		// ignored instead of generating (and thereby invalidating) a second order mid-payment.
		await this.repositoryService.runExclusivePayment(async () => {
			const generatedOrder = await this.generateOrder();
			await this.payOrder(generatedOrder);
		});
	}

	async startPaymentAll(): Promise<void> {
		console.log('startPaymentAll()');
		await this.repositoryService.runExclusivePayment(async () => {
			const generatedOrder = await this.generateOrderAll();
			await this.payOrder(generatedOrder);
		});
	}

	async generateOrder(): Promise<Order> {
		try {
			console.log('PaymentHandler.generateOrder()');
			const generatedOrder = await this.repositoryService.generateOrder();
			generatedOrder.venueReadableId = this.venueReadableId;
			generatedOrder.createdAt = dayjs();
			return generatedOrder;
		} catch (e) {
			const alert = await this.alertCtrl.create({
				header: this.t('paymentHandler.errorTitle'),
				message: this.t('paymentHandler.errorMessage'),
				buttons: [
					{
						text: this.t('paymentHandler.reload'),
						handler: () => {
							window.location.reload();
						},
					},
				],
			});
			await alert.present();
			throw e;
		}
	}

	async generateOrderAll(): Promise<Order> {
		try {
			console.log('PaymentHandler.generateOrderAll()');
			const generatedOrder = await this.repositoryService.generateOrderAll();
			generatedOrder.venueReadableId = this.venueReadableId;
			generatedOrder.createdAt = dayjs();
			return generatedOrder;
		} catch (e) {
			const alert = await this.alertCtrl.create({
				header: this.t('paymentHandler.errorTitle'),
				message: this.t('paymentHandler.errorMessage'),
				buttons: [
					{
						text: this.t('paymentHandler.reload'),
						handler: () => {
							window.location.reload();
						},
					},
				],
			});
			await alert.present();
			throw e;
		}
	}

	async payOrder(order: Order) {
		console.log('PaymentHandler.payOrder()', order);
		this.onLoad(true);
		try {
			const venue = await this.repositoryService.getVenue(order.venue);
			this.onLoad(false);
			const paymentMethods = paymentMethodsFromVenue(venue);
			let paySessionStatistics = await firstValueFrom(this.repositoryService.paySessionStatisticsObservable);

			// Show tip selection modal first
			const selectedTip = await TipSelectionModalComponent.show(
				this.modalCtrl,
				paySessionStatistics?.selectedTotalPrice ?? 0,
				order.checkoutOrderedArticles.length,
				paySessionStatistics
			);
			this.repositoryService.onTipsSelected(selectedTip);
			paySessionStatistics = await firstValueFrom(this.repositoryService.paySessionStatisticsObservable);

			const paymentMethod = await SelectPaymentMethodModalComponent.show(
				this.modalCtrl,
				paySessionStatistics,
				paymentMethods,
				order,
				venue.adyenCredentials
			);
			console.log({ paymentMethod });
			try {
				await this.repositoryService.setSocketTimeout(environment.socketTimeoutWhilePaymentInMins * 60 * 1000);
			} catch (error) {
				console.error(error);
			}

			// Get any added gift cards from the repository
			const addedGiftCards = await firstValueFrom(this.repositoryService.addedGiftCards);

			switch (paymentMethod) {
				case PaymentMethod.ADYEN:
					await this.payWithAdyen(order, venue.adyenCredentials, addedGiftCards, selectedTip);
					break;
				case PaymentMethod.PAYPAL_ADYEN:
					await this.payWithPayPal(order, addedGiftCards, selectedTip);
					break;
				case PaymentMethod.PAYPAL:
					await this.payWithPaypalNative(order, addedGiftCards, selectedTip);
					break;
				case PaymentMethod.CASH:
					await this.payWithCash(order, addedGiftCards, selectedTip);
					break;
				case PaymentMethod.PIGGY_GIFT_CARD:
					await this.payWithGiftCard(order, addedGiftCards, selectedTip);
					break;
				case PaymentMethod.APAY:
					await this.payWithApplePay(order, venue.adyenCredentials, addedGiftCards, selectedTip);
					break;
				case PaymentMethod.GPAY:
					await this.payWithGooglePay(order, venue.adyenCredentials, addedGiftCards, selectedTip);
					break;
				default:
					console.log(paymentMethod, 'not implemented yet');
			}
		} catch (e) {
		} finally {
			console.log('Resetting timeout to 180s after payment (finally)');
			try {
				await this.repositoryService.setSocketTimeout(180 * 1000);
			} catch (error) {
				console.error(error);
			}
		}
		this.onLoad(false);
	}

	async payWithCash(order: Order, giftCards: PiggyGiftCard[] = [], selectedTip: TipSelectorValue) {
		console.log('PaymentHandler.payWithCash()', order);
		const payment = paymentFromOrder(order);

		// Compute what is left to pay in cash after gift cards, for the "fully covered" check
		// and the modal display. When cash is still due we do NOT create the gift-card payment
		// here anymore: the gift cards are handed to the CashDro via the QR, and the CashDro side
		// creates the gift-card + cash payments (multiple) once the remaining cash is collected.
		const totalGiftCardBalance = giftCards.reduce((sum, card) => sum + card.balance, 0);
		const remainingCash = Math.max(0, numberD(payment.sum) - totalGiftCardBalance);

		if (remainingCash <= 0) {
			// Gift cards cover everything — no cash is collected, so settle the gift-card payment here.
			if (giftCards.length > 0) {
				const giftCardResult = this.repositoryService.generateGiftCardPayment(giftCards, payment);
				if (giftCardResult.payment.sum && giftCardResult.payment.sum > 0) {
					await this.repositoryService.createPayments([giftCardResult.payment]);
				}
			}
			this.repositoryService.clearGiftCards();
			await this.navigatePaymentSuccess();
			return;
		}

		payment.method = PaymentMethod.CASH;
		let paySessionStatistics = await firstValueFrom(this.repositoryService.paySessionStatisticsObservable);
		paySessionStatistics = this.getAdjustedPaySessionStatistics(paySessionStatistics, giftCards);

		const result = await CashPaymentModalComponent.show(this.modalCtrl, order, paySessionStatistics, selectedTip, giftCards);
		console.log('CashPayment Result', { result });
		switch (result) {
			case CashPaymentResult.SUCCESS:
				// Gift cards were redeemed by the CashDro side as part of the payment; drop them
				// from state so they can't be reused on a subsequent order.
				this.repositoryService.clearGiftCards();
				const paidOrder = await this.repositoryService.getOrder(order._id);
				this.repositoryService.paidOrder = paidOrder;
				await this.repositoryService.reloadFinishedPayment();
				await this.navigatePaymentSuccess();
				break;
			case CashPaymentResult.CANCELLED:
				try {
					const verifiedOrder = await this.repositoryService.getOrder(order._id);
					if (verifiedOrder.status === OrderStatus.FAILED) {
						await this.startPayment();
					} else {
						await this.payOrder(order);
					}
				} catch (error) {
					console.error(error);
					await this.startPayment();
				}
				break;
			case CashPaymentResult.FAILED:
				await this.navigatePaymentFailed();
				break;
			case CashPaymentResult.TIMEOUT:
				await PaymentTimeoutModalComponent.show(this.modalCtrl);
				break;
		}
	}

	async payWithGiftCard(order: Order, giftCards: PiggyGiftCard[] = [], selectedTip: TipSelectorValue) {
		console.log('PaymentHandler.payWithGiftCard()', order, giftCards);
		if (!giftCards || giftCards.length === 0) {
			console.log('No gift cards provided');
			return;
		}
		this.onLoad(true);
		try {
			const payment = await this.repositoryService.finishGiftCardPayment(order, giftCards, selectedTip);
			this.onLoad(false);
			if (payment.status === PaymentStatus.done || payment.status === PaymentStatus.reserved) {
				await this.navigatePaymentSuccess();
			} else if (payment.status === PaymentStatus.failed) {
				await this.navigatePaymentFailed();
			}
		} catch (e) {
			console.error(e);
			this.onLoad(false);
			await this.navigatePaymentFailed();
		}
	}

	async payWithPayPal(order: Order, giftCards: PiggyGiftCard[] = [], selectedTip: TipSelectorValue) {
		console.log('PaymentHandler.payWithPayPal()', order);
		const payment = paymentFromOrder(order);
		payment.method = PaymentMethod.PAYPAL;
		payment.isAdyenPaypal = true;

		// If gift cards are present, create gift card payment first, then PayPal for remainder
		if (giftCards.length > 0) {
			const giftCardResult = this.repositoryService.generateGiftCardPayment(giftCards, payment);
			if (giftCardResult.payment.sum && giftCardResult.payment.sum > 0) {
				await this.repositoryService.createPayments([giftCardResult.payment]);
			}
			payment.sum = giftCardResult.remainingValue;
		}

		if ((payment.sum ?? 0) > 0) {
			const createdPayment = await this.repositoryService.createPayment(payment);
			if (createdPayment.adyenPayment?.redirectUrl) {
				window.location.assign(createdPayment?.adyenPayment?.redirectUrl);
			}
		} else {
			// Gift cards covered everything
			this.repositoryService.clearGiftCards();
			await this.navigatePaymentSuccess();
		}
	}

	async payWithPaypalNative(order: Order, giftCards: PiggyGiftCard[] = [], selectedTip: TipSelectorValue) {
		console.log('PaymentHandler.payWithPaypalNative()', order);
		const payment = paymentFromOrder(order);
		const tipAbsoluteValue = getAbsolutValueFromTipSelection(payment.sum ?? 0, selectedTip);
		payment.method = PaymentMethod.PAYPAL;
		payment.isAdyenPaypal = false;
		payment.tip = tipAbsoluteValue;
		payment.preAuth = true;

		if (giftCards.length > 0) {
			const giftCardResult = this.repositoryService.generateGiftCardPayment(giftCards, payment);
			if (giftCardResult.payment.sum && giftCardResult.payment.sum > 0) {
				await this.repositoryService.createPayments([giftCardResult.payment]);
			}
			payment.sum = giftCardResult.remainingValue;
		}

		if ((payment.sum ?? 0) <= 0) {
			this.repositoryService.clearGiftCards();
			await this.navigatePaymentSuccess();
			return;
		}

		this.onLoad(true);
		let createdPayment;
		try {
			createdPayment = await this.repositoryService.createPayment(payment);
		} catch (e) {
			console.error('createPayment failed for PayPal', e);
			this.onLoad(false);
			await this.navigatePaymentFailed();
			return;
		}
		this.onLoad(false);

		if (!createdPayment?.paypal?.orderId) {
			console.error('Backend did not return paypal.orderId', createdPayment);
			await this.navigatePaymentFailed();
			return;
		}

		const paySessionStatistics = await firstValueFrom(this.repositoryService.paySessionStatisticsObservable);
		const adjustedStats = this.getAdjustedPaySessionStatistics(paySessionStatistics, giftCards);

		const result = await PaypalModalComponent.show(this.modalCtrl, adjustedStats, order, createdPayment);
		if (!result || result.result === PaypalModalResult.CANCELLED) {
			console.log('PayPal payment cancelled by user');
			return;
		}

		if (result.result === PaypalModalResult.FAILED) {
			await this.navigatePaymentFailed();
			return;
		}

		this.onLoad(true);
		try {
			const paidOrder = await this.repositoryService.getOrder(order._id);
			paidOrder.venueReadableId = order.venueReadableId;
			this.repositoryService.paidOrder = paidOrder;
			this.repositoryService.clearGiftCards();
			await this.repositoryService.reloadFinishedPayment();
			await this.navigatePaymentSuccess();
		} catch (e) {
			console.error('Error finalizing PayPal payment', e);
			await this.navigatePaymentFailed();
		} finally {
			this.onLoad(false);
		}
	}

	async payWithAdyen(order: Order, adyenCredentials: AdyenCredentials, giftCards: PiggyGiftCard[] = [], selectedTip: TipSelectorValue) {
		console.log('PaymentHandler.payWithAdyen()', order, adyenCredentials);
		let paySessionStatistics = await firstValueFrom(this.repositoryService.paySessionStatisticsObservable);
		paySessionStatistics = this.getAdjustedPaySessionStatistics(paySessionStatistics, giftCards);

		const result = await CreditCardModalComponent.show(this.modalCtrl, paySessionStatistics, [order], adyenCredentials);
		if (!result) {
			console.log('Payment was cancelled by user');
			return;
		}
		this.onLoad(true);
		try {
			const payment = await this.repositoryService.finishAdyenPayment(
				order,
				result.submitData,
				result.submitActions,
				selectedTip,
				giftCards
			);

			this.onLoad(false);
			if (payment.status === PaymentStatus.done || payment.status === PaymentStatus.reserved) {
				await this.navigatePaymentSuccess();
			} else if (payment.status === PaymentStatus.failed) {
				await this.navigatePaymentFailed();
			}
		} catch (e) {
			console.error(e);
			this.onLoad(false);
			await this.navigatePaymentFailed();
		}
	}

	async payWithGooglePay(
		order: Order,
		adyenCredentials: AdyenCredentials,
		giftCards: PiggyGiftCard[] = [],
		selectedTip: TipSelectorValue
	): Promise<void> {
		console.log('PaymentHandler.payWithGooglePay()', order, adyenCredentials);
		let paySessionStatistics = await firstValueFrom(this.repositoryService.paySessionStatisticsObservable);
		paySessionStatistics = this.getAdjustedPaySessionStatistics(paySessionStatistics, giftCards);

		const result = await GooglePayModalComponent.show(this.modalCtrl, paySessionStatistics, order, adyenCredentials);
		if (!result) {
			console.log('Payment was cancelled by user');
			return;
		}
		this.onLoad(true);
		try {
			const payment = await this.repositoryService.finishGooglePayAdyenPayment(
				order,
				result.googlePayToken,
				result.submitActions,
				selectedTip,
				giftCards
			);

			this.onLoad(false);
			if (payment.status === PaymentStatus.done || payment.status === PaymentStatus.reserved) {
				await this.navigatePaymentSuccess();
			} else if (payment.status === PaymentStatus.failed) {
				await this.navigatePaymentFailed();
			}
		} catch (e) {
			console.error(e);
			this.onLoad(false);
			await this.navigatePaymentFailed();
		}
	}

	async payWithApplePay(
		order: Order,
		adyenCredentials: AdyenCredentials,
		giftCards: PiggyGiftCard[] = [],
		selectedTip: TipSelectorValue
	): Promise<void> {
		let paySessionStatistics = await firstValueFrom(this.repositoryService.paySessionStatisticsObservable);
		paySessionStatistics = this.getAdjustedPaySessionStatistics(paySessionStatistics, giftCards);
		console.log({
			adjustedPaySessionStatistics: paySessionStatistics,
		});

		const result = await ApplePayModalComponent.show(this.modalCtrl, paySessionStatistics, order, adyenCredentials);
		const applePaySession = result.applePaySession;
		try {
			await new Promise(async (resolve, reject) => {
				applePaySession.onvalidatemerchant = async (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
					try {
						await this.repositoryService.validateApplePayment(order, applePaySession, event);
					} catch (e) {
						reject(e);
					}
				};

				applePaySession.onpaymentauthorized = async event => {
					try {
						const payment = await this.repositoryService.finishApplePayment(
							order,
							result.tipValue,
							applePaySession,
							event,
							giftCards
						);
						resolve(payment);
					} catch (e) {
						reject(e);
					}
				};
				applePaySession.oncancel = event => {
					reject(event);
				};
				applePaySession.begin();
			});
			await this.navigatePaymentSuccess();
		} catch (e) {
			console.error(e);
			await this.navigatePaymentFailed();
		}
	}

	async navigatePaymentSuccess() {
		// Check if we're running in iframe mode
		if (this.iframeCommunication && this.iframeCommunication.isInIframe()) {
			console.log('Payment successful in iframe mode - sending message to parent');

			// Get the paid order with all details
			const paidOrder = this.repositoryService.paidOrder;

			// Send a success message to the parent app
			this.iframeCommunication.sendPaymentSuccess(paidOrder?._id || 'success', {
				orderId: paidOrder?._id,
				venueReadableId: this.venueReadableId,
				timestamp: new Date().toISOString(),
				status: 'completed',
			});

			// The modal will close automatically in the parent app
			console.log('Success message sent - modal will close automatically');
		} else {
			// Standalone mode - navigate normally
			console.log('Payment successful in standalone mode - navigating to success page');
			await this.router.navigate(['/payment/success']);
		}
	}

	async navigatePaymentFailed() {
		// Check if we're running in iframe mode
		if (this.iframeCommunication && this.iframeCommunication.isInIframe()) {
			console.log('Payment failed in iframe mode - sending message to parent');

			// Send a failure message to the parent app
			this.iframeCommunication.sendPaymentFailed(this.t('paymentHandler.iframeFailed'));

			// The modal will close automatically in the parent app
			console.log('Failure message sent - modal will close automatically');
		} else {
			// Standalone mode - navigate normally
			console.log('Payment failed in standalone mode - navigating to fail page');
			await this.router.navigate(['/payment/fail']);
		}
	}

	private t(key: string): string {
		return this.translate?.instant(key) ?? key;
	}

	private getAdjustedPaySessionStatistics(stats: PaySessionStatistics | null, giftCards: PiggyGiftCard[]): PaySessionStatistics | null {
		if (!stats) return null;
		if (!giftCards || giftCards.length === 0) {
			return stats;
		}
		const totalGiftCardBalance = giftCards.reduce((sum, card) => sum + card.balance, 0);
		if (totalGiftCardBalance === 0) {
			return stats;
		}

		return {
			...stats,
			totalPrice: Math.max(0, stats.totalPrice - totalGiftCardBalance),
			selectedTotalPrice: Math.max(0, stats.selectedTotalPrice - totalGiftCardBalance),
		};
	}
}
